import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { APPOINTMENT_TYPES, getTypeLabel, isPersonalBlock } from "../../lib/appointmentStatus";
import {
  getAppointmentDurationMinutes,
  localFormToUtcIso,
  resolveBrowserTimezone,
  utcIsoToLocalDateAndTime,
} from "../../lib/agendaDateTime";

const EMPTY = {
  appointment_type: "office",
  patient_id: "",
  date: "",
  start_time: "09:00",
  duration_minutes: 30,
  buffer_before_minutes: 0,
  buffer_after_minutes: 0,
  reason_short: "",
  administrative_notes: "",
  address: "",
  institution: "",
  room: "",
  contact_phone: "",
  caregiver_name: "",
  caregiver_phone: "",
  reminder_enabled: false,
  reminder_channel: "none",
  communication_consent: false,
};

function activePatientsOnly(patients) {
  return (patients || []).filter((p) => {
    const st = String(p.status || "active").toLowerCase();
    return st !== "archived";
  });
}

function patientOptionLabel(p) {
  const name = [p.nombres, p.apellidos].filter(Boolean).join(" ").trim() || p.nombre || "Paciente";
  return name;
}

export default function AppointmentFormDialog({
  open,
  onOpenChange,
  mode = "create",
  appointment = null,
  patients = [],
  english = false,
  timeZone = resolveBrowserTimezone(),
  saving = false,
  showcaseMode = false,
  onSubmit,
  errorMessage = "",
}) {
  const [form, setForm] = useState(EMPTY);
  const [localError, setLocalError] = useState("");

  const activePatients = useMemo(() => activePatientsOnly(patients), [patients]);
  const personal = isPersonalBlock(form.appointment_type);

  useEffect(() => {
    if (!open) return;
    setLocalError("");
    if ((mode === "edit" || mode === "reschedule") && appointment) {
      const { date, time } = utcIsoToLocalDateAndTime(appointment.start_at, timeZone);
      const duration = getAppointmentDurationMinutes(appointment.start_at, appointment.end_at) || 30;
      setForm({
        appointment_type: appointment.appointment_type || "office",
        patient_id: appointment.patient_id || "",
        date,
        start_time: time,
        duration_minutes: duration,
        buffer_before_minutes: Number(appointment.buffer_before_minutes) || 0,
        buffer_after_minutes: Number(appointment.buffer_after_minutes) || 0,
        reason_short: appointment.reason_short || "",
        administrative_notes: appointment.administrative_notes || "",
        address: appointment.address || "",
        institution: appointment.institution || "",
        room: appointment.room || "",
        contact_phone: appointment.contact_phone || "",
        caregiver_name: appointment.caregiver_name || "",
        caregiver_phone: appointment.caregiver_phone || "",
        reminder_enabled: Boolean(appointment.reminder_enabled),
        reminder_channel: appointment.reminder_channel || "none",
        communication_consent: Boolean(appointment.communication_consent),
      });
    } else {
      const todayParts = utcIsoToLocalDateAndTime(new Date().toISOString(), timeZone);
      setForm({ ...EMPTY, date: todayParts.date });
    }
  }, [open, mode, appointment, timeZone]);

  const title =
    mode === "reschedule"
      ? english
        ? "Reschedule appointment"
        : "Reprogramar cita"
      : mode === "edit"
        ? english
          ? "Edit appointment"
          : "Editar cita"
        : english
          ? "New appointment"
          : "Nueva cita";

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = (event) => {
    event.preventDefault();
    setLocalError("");
    if (showcaseMode) {
      setLocalError(
        english
          ? "Design preview only — nothing is saved."
          : "Vista de diseño · no se guarda nada."
      );
      return;
    }
    try {
      const duration = Math.max(5, Number(form.duration_minutes) || 0);
      if (!form.date || !form.start_time) {
        setLocalError(english ? "Date and start time are required." : "Fecha y hora inicial son obligatorias.");
        return;
      }
      if (!personal && !form.patient_id) {
        setLocalError(english ? "Patient is required." : "El paciente es obligatorio.");
        return;
      }
      if (form.reminder_enabled) {
        if (!form.communication_consent) {
          setLocalError(
            english
              ? "Consent is required to enable reminder settings."
              : "Se requiere consentimiento para activar recordatorios."
          );
          return;
        }
        if (!form.reminder_channel || form.reminder_channel === "none") {
          setLocalError(
            english
              ? "Choose email, SMS, or WhatsApp."
              : "Elija email, SMS o WhatsApp."
          );
          return;
        }
      }
      const startIso = localFormToUtcIso(form.date, form.start_time, timeZone);
      const [hh, mm] = form.start_time.split(":").map(Number);
      const endMinutes = hh * 60 + mm + duration;
      const endH = Math.floor(endMinutes / 60) % 24;
      const endM = endMinutes % 60;
      const endDayOffset = Math.floor(endMinutes / 60) >= 24 ? 1 : 0;
      let endDate = form.date;
      if (endDayOffset) {
        const [y, m, d] = form.date.split("-").map(Number);
        const next = new Date(Date.UTC(y, m - 1, d + 1));
        endDate = `${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, "0")}-${String(next.getUTCDate()).padStart(2, "0")}`;
      }
      const endIso = localFormToUtcIso(
        endDate,
        `${String(endH).padStart(2, "0")}:${String(endM).padStart(2, "0")}`,
        timeZone
      );
      if (new Date(endIso) <= new Date(startIso)) {
        setLocalError(
          english ? "End time must be after start time." : "La hora final debe ser posterior a la inicial."
        );
        return;
      }

      const payload = {
        appointment_type: form.appointment_type,
        start_at: startIso,
        end_at: endIso,
        timezone: timeZone,
        buffer_before_minutes: Math.max(0, Number(form.buffer_before_minutes) || 0),
        buffer_after_minutes: Math.max(0, Number(form.buffer_after_minutes) || 0),
        reason_short: form.reason_short || null,
        administrative_notes: form.administrative_notes || null,
        address: form.address || null,
        institution: form.institution || null,
        room: form.room || null,
        contact_phone: form.contact_phone || null,
        caregiver_name: personal ? null : form.caregiver_name || null,
        caregiver_phone: personal ? null : form.caregiver_phone || null,
        reminder_enabled: Boolean(form.reminder_enabled),
        reminder_channel: form.reminder_enabled ? form.reminder_channel : "none",
        communication_consent: Boolean(form.communication_consent),
      };
      if (!personal) payload.patient_id = form.patient_id;
      if (personal) payload.patient_id = null;

      onSubmit?.(payload, { mode, appointment });
    } catch (err) {
      setLocalError(err?.message || (english ? "Invalid date/time." : "Fecha/hora no válida."));
    }
  };

  const showAddress = ["home", "nursing_home", "office"].includes(form.appointment_type);
  const showInstitution = ["hospital", "nursing_home"].includes(form.appointment_type);
  const showRoom = form.appointment_type === "hospital";
  const typeLocked = mode === "edit" || mode === "reschedule";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="appointment-form-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {english ? "Timezone" : "Zona horaria"}: {timeZone}
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="appointment_type">{english ? "Visit type" : "Tipo de atención"}</Label>
              <Select
                value={form.appointment_type}
                onValueChange={(v) => setField("appointment_type", v)}
                disabled={typeLocked || saving}
              >
                <SelectTrigger id="appointment_type" data-testid="appointment-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {APPOINTMENT_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {getTypeLabel(t, { english })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {!personal ? (
              <div className="space-y-2">
                <Label htmlFor="patient_id">{english ? "Patient" : "Paciente"}</Label>
                {mode === "create" ? (
                  activePatients.length === 0 ? (
                    <p className="text-sm text-slate-600">
                      {english ? "No active patients." : "No hay pacientes activos."}{" "}
                      <Link className="text-sky-700 underline" to="/pacientes">
                        {english ? "Go to patients" : "Ir a pacientes"}
                      </Link>
                    </p>
                  ) : (
                    <Select
                      value={form.patient_id}
                      onValueChange={(v) => setField("patient_id", v)}
                      disabled={saving}
                    >
                      <SelectTrigger id="patient_id" data-testid="appointment-patient">
                        <SelectValue placeholder={english ? "Select" : "Seleccionar"} />
                      </SelectTrigger>
                      <SelectContent>
                        {activePatients.map((p) => (
                          <SelectItem key={p.id} value={String(p.id)}>
                            {patientOptionLabel(p)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )
                ) : (
                  <Input id="patient_id" value={form.patient_id} disabled readOnly />
                )}
              </div>
            ) : null}
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="date">{english ? "Date" : "Fecha"}</Label>
              <Input
                id="date"
                type="date"
                value={form.date}
                onChange={(e) => setField("date", e.target.value)}
                disabled={saving || (mode === "edit" && appointment?.status === "in_progress")}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="start_time">{english ? "Start" : "Hora inicial"}</Label>
              <Input
                id="start_time"
                type="time"
                value={form.start_time}
                onChange={(e) => setField("start_time", e.target.value)}
                disabled={saving || (mode === "edit" && appointment?.status === "in_progress")}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="duration_minutes">{english ? "Duration (min)" : "Duración (min)"}</Label>
              <Input
                id="duration_minutes"
                type="number"
                min={5}
                step={5}
                value={form.duration_minutes}
                onChange={(e) => setField("duration_minutes", e.target.value)}
                disabled={saving || (mode === "edit" && appointment?.status === "in_progress")}
                required
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="buffer_before">{english ? "Buffer before" : "Buffer antes"}</Label>
              <Input
                id="buffer_before"
                type="number"
                min={0}
                value={form.buffer_before_minutes}
                onChange={(e) => setField("buffer_before_minutes", e.target.value)}
                disabled={saving || (mode === "edit" && appointment?.status === "in_progress")}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="buffer_after">{english ? "Buffer after" : "Buffer después"}</Label>
              <Input
                id="buffer_after"
                type="number"
                min={0}
                value={form.buffer_after_minutes}
                onChange={(e) => setField("buffer_after_minutes", e.target.value)}
                disabled={saving || (mode === "edit" && appointment?.status === "in_progress")}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason_short">
              {personal
                ? english
                  ? "Title"
                  : "Título administrativo"
                : english
                  ? "Short reason"
                  : "Motivo breve"}
            </Label>
            <Input
              id="reason_short"
              maxLength={200}
              value={form.reason_short}
              onChange={(e) => setField("reason_short", e.target.value)}
              disabled={saving}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="administrative_notes">{english ? "Admin notes" : "Notas administrativas"}</Label>
            <Textarea
              id="administrative_notes"
              rows={3}
              value={form.administrative_notes}
              onChange={(e) => setField("administrative_notes", e.target.value)}
              disabled={saving}
            />
          </div>

          {showAddress ? (
            <div className="space-y-2">
              <Label htmlFor="address">{english ? "Address" : "Dirección"}</Label>
              <Input
                id="address"
                value={form.address}
                onChange={(e) => setField("address", e.target.value)}
                disabled={saving}
              />
            </div>
          ) : null}

          {showInstitution ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="institution">{english ? "Institution" : "Institución"}</Label>
                <Input
                  id="institution"
                  value={form.institution}
                  onChange={(e) => setField("institution", e.target.value)}
                  disabled={saving}
                />
              </div>
              {showRoom ? (
                <div className="space-y-2">
                  <Label htmlFor="room">{english ? "Room" : "Habitación"}</Label>
                  <Input
                    id="room"
                    value={form.room}
                    onChange={(e) => setField("room", e.target.value)}
                    disabled={saving}
                  />
                </div>
              ) : null}
            </div>
          ) : null}

          {!personal ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="contact_phone">{english ? "Contact phone" : "Teléfono de contacto"}</Label>
                <Input
                  id="contact_phone"
                  value={form.contact_phone}
                  onChange={(e) => setField("contact_phone", e.target.value)}
                  disabled={saving}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="caregiver_name">{english ? "Caregiver" : "Cuidador / contacto"}</Label>
                <Input
                  id="caregiver_name"
                  value={form.caregiver_name}
                  onChange={(e) => setField("caregiver_name", e.target.value)}
                  disabled={saving}
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="caregiver_phone">{english ? "Caregiver phone" : "Teléfono del cuidador"}</Label>
                <Input
                  id="caregiver_phone"
                  value={form.caregiver_phone}
                  onChange={(e) => setField("caregiver_phone", e.target.value)}
                  disabled={saving}
                />
              </div>
            </div>
          ) : null}

          <fieldset className="space-y-3 rounded-lg border border-slate-200 p-3">
            <legend className="px-1 text-sm font-medium text-slate-800">
              {english ? "Reminder preparation" : "Preparación para recordatorios"}
            </legend>
            <p className="text-xs text-slate-500">
              {english
                ? "This setting does not send messages yet."
                : "Esta configuración no envía mensajes todavía."}
            </p>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.reminder_enabled}
                onChange={(e) => setField("reminder_enabled", e.target.checked)}
                disabled={saving}
              />
              {english ? "Prepare reminder" : "Preparar recordatorio"}
            </label>
            {form.reminder_enabled ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="reminder_channel">{english ? "Channel" : "Canal"}</Label>
                  <Select
                    value={form.reminder_channel}
                    onValueChange={(v) => setField("reminder_channel", v)}
                    disabled={saving}
                  >
                    <SelectTrigger id="reminder_channel">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="sms">SMS</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.communication_consent}
                    onChange={(e) => setField("communication_consent", e.target.checked)}
                    disabled={saving}
                  />
                  {english
                    ? "Communication consent"
                    : "Consentimiento de comunicación"}
                </label>
              </>
            ) : null}
          </fieldset>

          {(localError || errorMessage) && (
            <p className="text-sm text-rose-700" role="alert" data-testid="appointment-form-error">
              {localError || errorMessage}
            </p>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              {english ? "Cancel" : "Cerrar"}
            </Button>
            <Button type="submit" disabled={saving || (mode === "create" && !personal && activePatients.length === 0)} data-testid="appointment-form-submit">
              {saving
                ? english
                  ? "Saving…"
                  : "Guardando…"
                : english
                  ? "Save"
                  : "Guardar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
